import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { X, MessageCircle, Copy, ExternalLink, Info, Send, Check } from "lucide-react";
import { toast } from "sonner";
import { WHATSAPP_NUMBER, formatPrice } from "../data/products";
import { api } from "../lib/api";

export default function QuoteCart({
  open,
  onOpenChange,
  items,
  onRemove,
  name,
  setName,
  phone,
  setPhone,
  note,
  setNote,
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fallbackOpen, setFallbackOpen] = useState(false);
  const [adminSending, setAdminSending] = useState(false);
  const [adminSent, setAdminSent] = useState(false);

  const hasAnyFile = items.some((i) => i.hasFile);
  const subtotal = items.reduce((sum, i) => sum + (i.price || 0), 0);

  const buildMessage = () => {
    const lines = ["Hola, me gustaría cotizar:"];
    items.forEach((item, idx) => {
      let line = `${idx + 1}. ${item.brand ? item.brand + " — " : ""}${item.productName}`;
      if (item.price) line += ` (${item.priceFrom ? "desde " : ""}${formatPrice(item.price)})`;
      if (item.type === "producto") {
        line += ` · ${item.category}`;
      } else if (item.type === "reparacion") {
        line += ` · Reparación (pago en efectivo)`;
      } else {
        line += ` · Cristal: ${item.crystal}`;
        if (item.focal) line += ` · Focal: ${item.focal}`;
        if (item.color) line += ` · Color: ${item.color}`;
        line += ` · Receta: ${item.receta}`;
      }
      lines.push(line);
    });
    if (subtotal > 0) {
      lines.push("", `Subtotal referencial: ${formatPrice(subtotal)}`);
      lines.push("(Total final se ajusta según receta y cristales)");
    }
    if (hasAnyFile) lines.push("", "Adjuntaré mi receta en esta conversación.");
    if (name) lines.push(`Nombre: ${name}`);
    if (phone) lines.push(`Teléfono: ${phone}`);
    if (note) lines.push(`Nota: ${note}`);
    return lines.join("\n");
  };

  const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(buildMessage())}`;
  const waWebUrl = `https://web.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(buildMessage())}`;

  const handleCotizar = () => {
    if (items.length === 0) return;
    setConfirmOpen(true);
  };

  const handleConfirmed = () => {
    setConfirmOpen(false);
    // Guardar cotización en backend (best-effort, no bloquea WhatsApp)
    api.saveQuote({ items, name, phone, note, subtotal }).catch(() => {});
    // Intentar abrir; si está bloqueado mostrar fallback
    const w = window.open(waUrl, "_blank", "noopener,noreferrer");
    setTimeout(() => {
      if (!w || w.closed) setFallbackOpen(true);
    }, 400);
  };

  const handleSendToAdmin = async () => {
    if (items.length === 0 || adminSending) return;
    if (!name.trim() || !phone.trim()) {
      toast.error("Por favor ingresa tu nombre y teléfono");
      return;
    }
    setAdminSending(true);
    try {
      await api.saveQuote({ items, name, phone, note, subtotal });
      setAdminSent(true);
      toast.success("¡Cotización enviada!", {
        description: "Te contactaremos al teléfono que indicaste.",
      });
      setTimeout(() => setAdminSent(false), 4000);
    } catch (err) {
      toast.error("No pudimos enviar la cotización", {
        description: "Intenta nuevamente o usa el botón de WhatsApp.",
      });
    } finally {
      setAdminSending(false);
    }
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(buildMessage());
      toast.success("Mensaje copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md bg-[#FAF9F6] border-l border-[#EAEAEA] p-0 flex flex-col"
          data-testid="quote-cart-sheet"
        >
          <SheetHeader className="p-8 pb-6 border-b border-[#EAEAEA] text-left">
            <span className="eyebrow">Tu selección</span>
            <SheetTitle className="font-serif-display text-3xl text-[#1C1C1C]">
              Solicitud de cotización
            </SheetTitle>
            <SheetDescription className="text-sm text-[#595959]">
              Revisa los productos y envía la consulta directo por WhatsApp.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-8 py-6">
            {items.length === 0 ? (
              <div className="text-center py-20" data-testid="cart-empty">
                <p className="text-sm text-[#595959] mb-2">
                  Aún no has agregado nada.
                </p>
                <p className="eyebrow">Elige un producto para comenzar</p>
              </div>
            ) : (
              <ul className="space-y-5">
                {items.map((item, idx) => (
                  <li
                    key={`${item.productId}-${idx}`}
                    className="flex gap-3 items-start justify-between border-b border-[#EAEAEA] pb-5"
                    data-testid={`cart-item-${idx}`}
                  >
                    <div className="flex-1">
                      <div className="eyebrow text-[10px] mb-1 text-[#B8933A]">
                        {item.type === "reparacion"
                          ? "Servicio"
                          : item.brand
                            ? item.brand
                            : item.type === "producto"
                              ? item.category
                              : "Lente óptico"}
                      </div>
                      <div className="font-serif-display text-xl text-[#1C1C1C] leading-tight mb-1">
                        {item.productName}
                      </div>
                      {item.type === "optico" && (
                        <div className="text-xs text-[#595959] leading-relaxed">
                          {item.crystal}
                          {item.focal && ` · ${item.focal}`}
                          {item.color && ` · ${item.color}`}
                          {item.receta && ` · Receta: ${item.receta}`}
                          {item.hasFile && ` · Archivo: ${item.fileName}`}
                        </div>
                      )}
                      {item.price && (
                        <div className="text-sm text-[#1C1C1C] mt-1 font-medium">
                          {item.priceFrom ? "Desde " : ""}{formatPrice(item.price)}
                          {item.type === "reparacion" && (
                            <span className="text-[10px] text-[#B8933A] ml-2 tracking-wider uppercase">Efectivo</span>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => onRemove(idx)}
                      className="text-[#595959] hover:text-[#1C1C1C] transition-colors mt-1"
                      aria-label="Quitar"
                      data-testid={`cart-remove-${idx}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border-t border-[#EAEAEA] p-8 space-y-5 bg-[#FAF9F6]">
            {subtotal > 0 && (
              <div className="flex items-baseline justify-between pb-3 border-b border-[#EAEAEA]">
                <span className="eyebrow">Subtotal referencial</span>
                <span
                  className="font-serif-display text-2xl text-[#1C1C1C]"
                  data-testid="cart-subtotal"
                >
                  {formatPrice(subtotal)}
                </span>
              </div>
            )}
            <div>
              <span className="eyebrow block mb-2">Nombre</span>
              <input type="text" placeholder="Tu nombre completo" value={name} onChange={(e) => setName(e.target.value)} className="minimal-input" data-testid="cart-name-input" />
            </div>
            <div>
              <span className="eyebrow block mb-2">Teléfono</span>
              <input type="tel" placeholder="+56 9 ..." value={phone} onChange={(e) => setPhone(e.target.value)} className="minimal-input" data-testid="cart-phone-input" />
            </div>
            <div>
              <span className="eyebrow block mb-2">Nota (opcional)</span>
              <input type="text" placeholder="Ej: urgencia, horario preferente…" value={note} onChange={(e) => setNote(e.target.value)} className="minimal-input" data-testid="cart-note-input" />
            </div>

            <button
              type="button"
              onClick={handleCotizar}
              disabled={items.length === 0}
              className="btn-whatsapp disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="whatsapp-quote-button"
            >
              <MessageCircle className="w-4 h-4" />
              Cotizar por WhatsApp
            </button>

            <button
              type="button"
              onClick={handleSendToAdmin}
              disabled={items.length === 0 || adminSending || adminSent}
              className="w-full flex items-center justify-center gap-2 border border-[#1C1C1C] text-[#1C1C1C] hover:bg-[#1C1C1C] hover:text-white transition-colors py-3 px-6 text-xs tracking-[0.22em] uppercase font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="send-to-admin-button"
            >
              {adminSent ? (
                <>
                  <Check className="w-4 h-4" />
                  Enviada al administrador
                </>
              ) : adminSending ? (
                <>Enviando…</>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Enviar al administrador
                </>
              )}
            </button>
            <p className="text-[11px] text-[#9A9A9A] text-center tracking-wide">
              Te responderemos dentro de los próximos minutos.
            </p>
          </div>
        </SheetContent>
      </Sheet>

      {/* Modal informativo antes de enviar */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-lg bg-[#FAF9F6] border border-[#EAEAEA] rounded-none" data-testid="confirm-dialog">
          <DialogHeader className="text-left">
            <div className="flex items-center gap-2 mb-2">
              <Info className="w-4 h-4 text-[#B8933A]" />
              <span className="eyebrow text-[#B8933A]">Antes de enviar</span>
            </div>
            <DialogTitle className="font-serif-display text-2xl text-[#1C1C1C]">
              El subtotal es referencial
            </DialogTitle>
            <DialogDescription className="text-sm text-[#595959] leading-relaxed pt-2">
              El subtotal mostrado corresponde al <strong>marco y accesorios o servicios</strong> seleccionados.
              Al revisar la información que nos envíes, te responderemos con el <strong>total real</strong>,
              ya que éste se cotiza según la <strong>receta y los cristales elegidos</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="h-[1px] bg-gradient-to-r from-[#B8933A] via-[#E9C46A] to-[#B8933A] my-2" />
          <DialogFooter className="flex sm:justify-end gap-2">
            <button type="button" onClick={() => setConfirmOpen(false)} className="btn-outline" data-testid="confirm-cancel">
              Cancelar
            </button>
            <button type="button" onClick={handleConfirmed} className="btn-primary" data-testid="confirm-understood">
              Entiendo
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fallback si WhatsApp está bloqueado */}
      <Dialog open={fallbackOpen} onOpenChange={setFallbackOpen}>
        <DialogContent className="max-w-xl bg-[#FAF9F6] border border-[#EAEAEA] rounded-none" data-testid="wa-fallback-dialog">
          <DialogHeader className="text-left">
            <span className="eyebrow text-[#B8933A]">¿WhatsApp bloqueado?</span>
            <DialogTitle className="font-serif-display text-2xl text-[#1C1C1C]">
              Te dejamos el mensaje listo para enviar
            </DialogTitle>
            <DialogDescription className="text-sm text-[#595959]">
              Si tu navegador bloqueó api.whatsapp.com, copia el mensaje y pégalo en el chat con nosotros.
            </DialogDescription>
          </DialogHeader>
          <textarea
            readOnly
            value={buildMessage()}
            className="w-full h-48 border border-[#EAEAEA] bg-white p-4 text-xs font-mono text-[#1C1C1C] resize-none"
            data-testid="wa-fallback-textarea"
          />
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <button type="button" onClick={copyMessage} className="btn-outline flex items-center gap-2" data-testid="wa-copy">
              <Copy className="w-4 h-4" />
              Copiar mensaje
            </button>
            <a href={waWebUrl} target="_blank" rel="noopener noreferrer" className="btn-primary flex items-center gap-2 justify-center" data-testid="wa-web-link">
              <ExternalLink className="w-4 h-4" />
              Abrir WhatsApp Web
            </a>
            <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="btn-primary flex items-center gap-2 justify-center" data-testid="wa-direct-link">
              <MessageCircle className="w-4 h-4" />
              Chat directo
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
